/**
 * Shared print utilities for Fresh Meet printables.
 */

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/** Returns the logo URL for print windows. */
export function getLogoUrl() {
  return `${window.location.origin}/fresh-meet-logo.png`;
}

/** Base CSS for print windows (controls, body, print media). */
const BASE_PRINT_CSS = `
  * { box-sizing: border-box; }
  body { margin: 0; font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 12px; color: #111827; }
  .controls { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
  .controls button { padding: 6px 10px; margin-right: 8px; cursor: pointer; border-radius: 4px; border: 1px solid #d1d5db; background: #fff; }
  h1 { font-size: 1.1rem; margin: 0 0 4px 0; }
  h2 { font-size: 0.95rem; margin: 0 0 4px 0; }
  @media print {
    .controls { display: none !important; }
  }
`;

/** Avery 5163: 2" x 4" labels (2in tall, 4in wide), 10 per sheet (2 cols x 5 rows), 8.5" x 11" paper. */
export function getAvery5163PrintCss() {
  return `
    @page { size: 8.5in 11in; margin: 0; }
    body { margin: 0; padding: 0; }
    .avery-5163-sheet {
      width: 8.5in;
      height: 11in;
      padding: 0.5in 0.15in;
      box-sizing: border-box;
      display: grid;
      grid-template-columns: 4in 4in;
      grid-template-rows: repeat(5, 2in);
      column-gap: 0.18in;
      row-gap: 0;
      margin: 0;
      page-break-after: always;
    }
    .avery-5163-sheet:last-child { page-break-after: auto; }
    .avery-5163-label {
      width: 4in;
      height: 2in;
      box-sizing: border-box;
      padding: 0.1in 0.15in;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-size: 10pt;
      line-height: 1.25;
      page-break-inside: avoid;
    }
    .avery-5163-label--freshling { background: #dbeafe; border: 1px solid #93c5fd; }
    .avery-5163-label--mentor { background: #fee2e2; border: 1px solid #fca5a5; }
    .avery-5163-label--blank { background: #f3f4f6; border: 1px dashed #9ca3af; }
    .avery-5163-label .label-name { font-weight: 700; font-size: 12pt; margin-bottom: 0.1in; }
    .avery-5163-label .label-dz { color: #374151; }
    .avery-5163-label .label-size { font-size: 10pt; margin-top: 0.05in; }
  `;
}

/** Badge variant: white background, colored text (blue freshling, red mentor), centered, large name. */
export function getAvery5163BadgeCss() {
  return `
    @page { size: 8.5in 11in; margin: 0; }
    body { margin: 0; padding: 0; }
    .avery-5163-sheet {
      width: 8.5in; height: 11in; padding: 0.5in 0.15in; box-sizing: border-box;
      display: grid; grid-template-columns: 4in 4in; grid-template-rows: repeat(5, 2in);
      column-gap: 0.18in; row-gap: 0; margin: 0; page-break-after: always;
    }
    .avery-5163-sheet:last-child { page-break-after: auto; }
    .avery-5163-label {
      width: 4in; height: 2in; box-sizing: border-box; padding: 0.15in;
      display: flex; flex-direction: column; justify-content: center; align-items: center;
      text-align: center; page-break-inside: avoid;
    }
    .avery-5163-label--freshling { background: #fff; border: none; color: #1d4ed8; }
    .avery-5163-label--mentor { background: #fff; border: none; color: #b91c1c; }
    .avery-5163-label--blank { background: #fafafa; border: none; color: #9ca3af; }
    .avery-5163-label .label-name { font-size: 18pt; font-weight: 700; letter-spacing: 0.02em; margin-bottom: 0.12in; line-height: 1.2; }
    .avery-5163-label .label-dz { font-size: 11pt; font-weight: 500; }
  `;
}

/** Bag label variant: like badges – white background, colored text, centered, big name, size only (no dropzone). */
export function getAvery5163BagLabelCss() {
  return `
    @page { size: 8.5in 11in; margin: 0; }
    body { margin: 0; padding: 0; }
    .avery-5163-sheet {
      width: 8.5in; height: 11in; padding: 0.5in 0.15in; box-sizing: border-box;
      display: grid; grid-template-columns: 4in 4in; grid-template-rows: repeat(5, 2in);
      column-gap: 0.18in; row-gap: 0; margin: 0; page-break-after: always;
    }
    .avery-5163-sheet:last-child { page-break-after: auto; }
    .avery-5163-label {
      width: 4in; height: 2in; box-sizing: border-box; padding: 0.15in;
      display: flex; flex-direction: column; justify-content: center; align-items: center;
      text-align: center; page-break-inside: avoid;
    }
    .avery-5163-label--freshling { background: #fff; border: none; color: #1d4ed8; }
    .avery-5163-label--mentor { background: #fff; border: none; color: #b91c1c; }
    .avery-5163-label--volunteer { background: #fff; border: none; color: #15803d; }
    .avery-5163-label--blank { background: #fafafa; border: none; color: #9ca3af; }
    .avery-5163-label .label-name { font-size: 18pt; font-weight: 700; letter-spacing: 0.02em; margin-bottom: 0.12in; line-height: 1.2; }
    .avery-5163-label .label-size { font-size: 14pt; font-weight: 600; margin-top: 0.05in; }
    .avery-5163-label .label-size--with-note {
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.06in;
      font-size: 14pt; font-weight: 600; text-align: center;
    }
    .avery-5163-label .label-size-with-star { display: inline-block; }
    .avery-5163-label .label-size-star { color: #b45309; font-weight: 700; font-size: 14pt; }
    .avery-5163-label .bag-label-footnote {
      font-size: 6pt; font-weight: 400; line-height: 1.25; max-width: 3.2in; color: #374151;
    }
    .avery-5163-label .bag-label-exchange {
      font-size: 5.5pt; font-weight: 400; line-height: 1.2; margin-top: auto; padding-top: 0.06in;
      max-width: 3.4in; text-align: center; color: #4b5563;
    }
    .avery-5163-label { position: relative; }
    .avery-5163-label .bag-label-goody-x {
      position: absolute; top: 0.06in; right: 0.1in;
      font-size: 14pt; font-weight: 700; color: inherit; line-height: 1;
    }
  `;
}

/** SkyQuest Freshling cards: 2.5" x 3.5" cards, 9-up (3x3) on letter paper. */
export function getSkyQuestCardCss() {
  return `
    @page { size: letter; margin: 0 !important; }
    html, body { margin: 0 !important; padding: 0 !important; width: 8.5in; }
    .skyquest-sheet {
      position: relative;
      width: 8.5in;
      height: 11in;
      page-break-after: always;
    }
    .skyquest-sheet:last-child { page-break-after: auto; }
    .skyquest-card {
      position: absolute;
      width: 2.16in;
      height: 3.06in;
      box-sizing: border-box;
      background: #ffffff;
      overflow: hidden;
      border-radius: 0.15in;
      border: 1px solid #1e3a8a;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      color: #111827;
    }
    /* 3 columns: cards centered in 3 equal boxes with slightly tighter (≈11mm) side margins */
    .skyquest-card.col0 { left: 0.60in; }
    .skyquest-card.col1 { left: 3.17in; }
    .skyquest-card.col2 { left: 5.74in; }
    /* 3 rows: cards centered in 3 equal boxes with slightly tighter (≈5mm) top/bottom margins */
    .skyquest-card.row0 { top: 0.41in; }
    .skyquest-card.row1 { top: 3.97in; }
    .skyquest-card.row2 { top: 7.53in; }
    @media print {
    }

    /* Card inner content (no extra square border; rely on outer rounded edge) */
    .skyquest-card-inner {
      position: relative;
      width: 100%;
      height: 100%;
      padding: 0.08in;
      box-sizing: border-box;
    }

    .skyquest-card-content {
      position: relative;
      width: 100%;
      height: 100%;
      padding: 0.12in;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 0.06in;
    }

    /* Top logo row */
    .skyquest-logo-row {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.12in;
      flex-shrink: 0;
      padding-top: 0.12in; /* make room for overlapped SkyQuest logo */
    }
    .skyquest-logo-row img {
      max-height: 0.32in;
      max-width: 1.2in;
      width: auto;
      height: auto;
      object-fit: contain;
    }
    /* SkyQuest logo (left) overlaps the upper-left corner of the card */
    .skyquest-logo-row img:first-child {
      position: absolute;
      top: -0.10in;
      left: -0.04in;
    }
    /* Fresh Meet logo sits near the upper-right corner with slight inset */
    .skyquest-logo-row img:last-child {
      align-self: flex-start;
      margin-top: 0.12in;  /* ~0.10–0.15in down */
      margin-right: 0.10in; /* ~0.08–0.12in in from right */
    }

    /* Formation panel */
    .skyquest-formation-panel {
      position: relative;
      flex: 1;
      background: #ffffff;
      border-radius: 4px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.06in 0.08in;
      box-sizing: border-box;
      margin-top: 0.02in;
      margin-bottom: 0.02in;
    }
    .skyquest-formation-panel img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      display: block;
    }
    .skyquest-modifier {
      position: absolute;
      left: 0.02in;
      top: 0.06in;
      bottom: 0.06in;
      display: flex;
      align-items: center;
      justify-content: center;
      writing-mode: vertical-rl;
      text-orientation: mixed;
      font-size: 6pt;
      letter-spacing: 0.06em;
      color: #1f2937;
      text-transform: uppercase;
    }

    /* Bottom bar */
    .skyquest-bottom-bar {
      margin-top: 0.02in;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      gap: 0.01in;
    }
    .skyquest-participant-name {
      font-size: 6.5pt;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #4b5563;
      min-height: 0.12in;
    }
    .skyquest-bottom-main {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.06in;
    }
    .skyquest-formation-name {
      font-size: 13pt;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #111827;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .skyquest-point-value {
      font-size: 16pt;
      font-weight: 800;
      color: #1e3a8a;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      min-width: 0.4in;
    }
    .skyquest-frac {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      line-height: 1;
      font-size: 80%;
    }
    .skyquest-frac-num {
      border-bottom: 1px solid currentColor;
      padding: 0 0.01in 0.01in;
    }
    .skyquest-frac-den {
      padding: 0.01in 0 0;
    }
  `;
}

/**
 * Avery 5874 passcode cards. Each card is absolutely positioned on the page
 * so browser print margins cannot shift the grid. 10 cards per page (2 cols x 5 rows).
 * Avery 5874: card 3.5" x 2", top/bottom margin 1.25", left/right 0.8125", h-gap 0.5", v-gap 0.
 * Content is inset 0.1" inside each card so it never bleeds to the edge.
 */
export function getAvery5874BusinessCardCss() {
  return `
    @page { size: letter; margin: 0 !important; }
    html, body { margin: 0 !important; padding: 0 !important; width: 8.5in; }
    .avery-5371-sheet {
      position: relative;
      width: 8.5in; height: 11in;
      page-break-after: always;
    }
    .avery-5371-sheet:last-child { page-break-after: auto; }
    .avery-5371-card {
      position: absolute;
      width: 3.5in; height: 2in; box-sizing: border-box;
      overflow: hidden;
      border: 1px solid #fdba74; border-radius: 4px;
      background: #fff;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      display: flex; flex-direction: row;
    }
    /* Column 1: left = 0.8125in. Column 2 nudged further left so QR stays comfortably clear of right cut. */
    .avery-5371-card.col0 { left: 0.8125in; }
    .avery-5371-card.col1 { left: 4.35in; }
    /* Rows: first row 0.75in from top (was 1.25), then +2in per row */
    .avery-5371-card.row0 { top: 0.75in; }
    .avery-5371-card.row1 { top: 2.75in; }
    .avery-5371-card.row2 { top: 4.75in; }
    .avery-5371-card.row3 { top: 6.75in; }
    .avery-5371-card.row4 { top: 8.75in; }
    @media print {
      .avery-5371-card { border: none; border-radius: 0; }
    }
    .avery-5371-card .passcode-card-left {
      flex: 1; min-width: 0; display: flex; flex-direction: column;
    }
    .avery-5371-card .passcode-card-top {
      background: #f97316; padding: 0.03in 0.06in; display: flex; align-items: center; justify-content: center; gap: 0.03in; flex-shrink: 0;
    }
    .avery-5371-card .passcode-card-logo {
      height: 0.16in; width: auto; object-fit: contain; filter: brightness(0) invert(1);
    }
    .avery-5371-card .passcode-card-brand {
      font-size: 7pt; font-weight: 700; letter-spacing: 0.05em; color: #fff; text-transform: uppercase;
    }
    .avery-5371-card .passcode-card-main {
      flex: 1; padding: 0.05in 0.08in; min-height: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;
    }
    .avery-5371-card .passcode-card-team {
      font-size: 11pt; font-weight: 800; color: #292524; letter-spacing: 0.02em; text-transform: uppercase; line-height: 1.1;
    }
    .avery-5371-card .passcode-card-divider {
      width: 100%; max-width: 1.2in; height: 1px; background: #d6d3d1; margin: 0.03in 0;
    }
    .avery-5371-card .passcode-card-code-label {
      font-size: 6pt; font-weight: 700; color: #f97316; letter-spacing: 0.04em; text-transform: uppercase;
    }
    .avery-5371-card .passcode-card-code {
      font-size: 12pt; font-weight: 800; color: #fb923c; letter-spacing: 0.06em; line-height: 1.15;
      text-transform: uppercase; margin: 0.01in 0; word-break: break-all;
    }
    .avery-5371-card .passcode-card-msg {
      font-size: 5.5pt; color: #57534e; line-height: 1.2; margin-bottom: 0;
    }
    .avery-5371-card .passcode-card-bottom {
      background: #f97316; padding: 0.025in 0.06in; text-align: center; flex-shrink: 0;
      font-size: 5pt; font-weight: 600; letter-spacing: 0.02em; color: #fff; text-transform: uppercase;
    }
    .avery-5371-card .passcode-card-qr-wrap {
      width: 1.25in; flex-shrink: 0; display: flex; align-items: center; justify-content: center; padding: 0.04in;
      border-left: 1px solid #fdba74;
    }
    @media print { .avery-5371-card .passcode-card-qr-wrap { border-left: 1px solid #ddd; } }
    .avery-5371-card .passcode-card-qr {
      width: 1in; height: 1in; max-width: 100%; max-height: 100%;
      display: flex; align-items: center; justify-content: center;
    }
    .avery-5371-card .passcode-card-qr img { width: 100%; height: 100%; object-fit: contain; }
    .print-instructions {
      padding: 10px 14px; background: #fef3c7; border-bottom: 1px solid #f59e0b;
      font-size: 13px; color: #92400e; font-weight: 500;
    }
    @media print { .print-instructions { display: none !important; } }
  `;
}

/**
 * @typedef {object} OpenPrintWindowOptions
 * @property {string} title
 * @property {string} bodyHtml
 * @property {string} [extraCss]
 * @property {string} [returnUrl]
 * @property {boolean} [autoPrint]
 */

/**
 * Opens a print window with shared chrome (Print / Close).
 * @param {OpenPrintWindowOptions} opts
 * @returns {boolean} false if popup was blocked
 */
export function openPrintWindowOptions(opts) {
  const {
    title,
    bodyHtml,
    extraCss = '',
    returnUrl = '',
    autoPrint = false,
  } = opts;
  const win = window.open('', '_blank');
  if (!win) return false;
  const safeTitle = title || 'Print';
  const allCss = BASE_PRINT_CSS + (extraCss || '');
  const safeReturnUrl = returnUrl || '';
  const closeHandler = safeReturnUrl
    ? `if(window.opener){try{window.opener.location.href=${JSON.stringify(safeReturnUrl)};window.opener.focus();}catch(e){}}window.close();`
    : 'window.close();';
  const closeHandlerAttr = closeHandler.replace(/"/g, '&quot;');
  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${safeTitle}</title>
<style>${allCss}</style></head>
<body>
  <div class="controls">
    <button onclick="window.print()">Print</button>
    <button onclick="${closeHandlerAttr}">Close</button>
  </div>
  ${bodyHtml}
</body></html>`;
  win.document.write(html);
  win.document.close();
  if (autoPrint) {
    setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch {
        // ignore
      }
    }, 250);
  }
  return true;
}

/**
 * Opens a print window with the given title, body HTML, and optional extra CSS.
 * Triggers the browser print dialog on the new window after a short delay (same as openPrintWindowOptions autoPrint).
 * @param {string} title - Document title
 * @param {string} bodyHtml - HTML for the body content
 * @param {string} [extraCss] - Additional CSS (e.g. Avery 5163 layout)
 * @param {string} [returnUrl] - URL to send the opener to when Close is clicked (e.g. /admin/printables)
 * @returns {boolean} false if the popup was blocked
 */
export function openPrintWindow(title, bodyHtml, extraCss = '', returnUrl = '') {
  const ok = openPrintWindowOptions({ title, bodyHtml, extraCss, returnUrl, autoPrint: true });
  if (!ok) {
    window.alert('Popup blocked. Please allow popups and try again.');
  }
  return ok;
}

/** CSS for simple operational tables inside print windows. */
export function getOperationalTablePrintCss() {
  return `
    body { margin: 0.4in; }
    table { width: 100%; border-collapse: collapse; font-size: 11pt; }
    th, td { border: 1px solid #999; padding: 4px 8px; text-align: left; }
    th { background: #eee; }
    tbody tr:nth-child(even) { background: #f9fafb; }
    @media print {
      tbody tr:nth-child(even) {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  `;
}

/** Letter page size in mm (for jsPDF). */
const LETTER_W_MM = 215.9;
const LETTER_H_MM = 279.4;

/**
 * Build a PDF from SkyQuest card sheet HTML for accurate print dimensions.
 * Renders each .skyquest-sheet at CSS 8.5"×11", captures with html2canvas, adds one page per sheet.
 * @param {string} bodyHtml - HTML containing .skyquest-sheet divs (same as print window body)
 * @param {string} extraCss - getSkyQuestCardCss() output
 * @param {string} [filename] - Download filename
 * @returns {Promise<void>}
 */
export async function buildSkyQuestPdf(bodyHtml, extraCss, filename = 'skyquest-freshling-cards.pdf') {
  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;width:8.5in;height:11in;';
  container.innerHTML = `<style>${extraCss}</style>${bodyHtml}`;
  document.body.appendChild(container);

  const inlineImagesAsDataUrls = async (el) => {
    const imgs = el.querySelectorAll ? el.querySelectorAll('img') : [];
    await Promise.all(
      Array.from(imgs).map(async (img) => {
        const src = img.getAttribute('src') || '';
        if (!src || src.startsWith('data:')) return;
        try {
          const res = await fetch(src, { mode: 'cors' });
          if (!res.ok) return;
          const blob = await res.blob();
          await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              img.src = reader.result;
              resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          // Best-effort: if we can't inline, leave the src as-is.
        }
      })
    );
  };

  const waitForImages = (el) => {
    const imgs = el.querySelectorAll ? el.querySelectorAll('img') : [];
    return Promise.all(
      Array.from(imgs).map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              })
      )
    );
  };

  try {
    await inlineImagesAsDataUrls(container);
    await waitForImages(container);
    const sheets = container.querySelectorAll('.skyquest-sheet');
    if (!sheets.length) {
      throw new Error('No .skyquest-sheet elements found');
    }
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'letter' });
    for (let i = 0; i < sheets.length; i++) {
      const sheetEl = sheets[i];
      const canvas = await html2canvas(sheetEl, {
        scale: 1,
        useCORS: false,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
      });
      if (i > 0) pdf.addPage();
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, LETTER_W_MM, LETTER_H_MM, undefined, 'FAST');
    }
    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
}
